import { message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import Icon from '@ant-design/icons';
import { useGetMappedEvents } from '@api/emp/queryHooks';
import { trimMappedEventRules } from '@components/mapping-panel/utils/trimUtils';
import { validateMappedEventData } from '@components/mapping-panel/utils/validateUtils';
import SetSvg from '@icons/bad-event-drawer/set.svg';
import {
  useCreateActivateMappedEvent,
  useUpdateActivateMappedEvent,
} from '@src/emp-3.0/api/queryHooks';
import { Body, Header } from '@src/emp-3.0/components/BadEventDrawer/styled';
import MappedEventBuilder from '@src/emp-3.0/components/MappedEventBuilder';
import { getInitSimpleMappedEvent } from '@src/emp-3.0/utils';
import { IMappedEvent } from '@src/pages/metric-templates/types';
import { cloneDeep, isEqual } from 'lodash';

const badEventName = 'Bad Event';
const defaultMappedEvent = {
  ...getInitSimpleMappedEvent(badEventName, 'Custom'),
};
const BadEventManagement = () => {
  const { data: allMappedEventsData, isLoading: isGetAllEventsLoading } = useGetMappedEvents({
    preferred_version: 'deployed',
    need_bad_event: 1,
  });
  const { mutate: activateMappedEvent, isLoading: isActivateMappedEventLoading } =
    useCreateActivateMappedEvent();
  const { mutate: updateMappedEvent, isLoading: isUpdateMappedEventLoading } =
    useUpdateActivateMappedEvent();

  const isLoading = useMemo(
    () => isGetAllEventsLoading || isActivateMappedEventLoading || isUpdateMappedEventLoading,
    [isGetAllEventsLoading, isActivateMappedEventLoading, isUpdateMappedEventLoading],
  );
  const cloneOptions = useMemo(
    () => (allMappedEventsData?.data || []).filter((event) => event.event_name !== badEventName),
    [allMappedEventsData],
  );
  const badEventExists = useMemo(
    () => (allMappedEventsData?.data || []).find((event) => event.event_name === badEventName),
    [allMappedEventsData],
  );

  const [badEvent, setBadEvent] = useState<IMappedEvent>(badEventExists || defaultMappedEvent);

  useEffect(() => {
    const badEventExists = (allMappedEventsData?.data || []).find(
      (event) => event.event_name === badEvent.event_name,
    );
    if (!badEventExists) {
      return;
    }
    const copiedBadEvent = cloneDeep(badEventExists);
    const { event_id, event_name, event_category, description, rules } = copiedBadEvent;
    setBadEvent({ event_id, event_name, event_category, description, rules });
  }, [allMappedEventsData]);

  const handleReset = () => {
    let newBadEvent = { ...getInitSimpleMappedEvent('Bad Event', 'Custom') };
    if (badEventExists) {
      const { event_id, event_name, event_category, description, rules } =
        cloneDeep(badEventExists);
      newBadEvent = { event_id, event_name, event_category, description, rules };
    }

    setBadEvent(newBadEvent);
  };

  const handleSuccess = () => {
    message.success('Mapping Successful !').then();
  };

  const handleError = (error: any) => {
    const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
    message.error(errorMessage);
  };

  const handleMapClick = (event) => {
    if (event) {
      const res = validateMappedEventData(event, true);
      if (!res.status) {
        message.warning(`${res.key ? res.key.join(' OR ') : ''} ${res.result}`);
        return;
      }

      if (event.event_id) {
        updateMappedEvent(
          { event_id: event.event_id, queryBody: event },
          {
            onSuccess: () => handleSuccess(),
            onError: handleError,
          },
        );
      } else {
        activateMappedEvent(event, {
          onSuccess: () => handleSuccess(),
          onError: handleError,
        });
      }
    }
  };

  return (
    <div>
      <Header>
        <div style={{ padding: '8px 0 0 24px' }}>
          <Icon component={SetSvg} style={{ marginRight: '8px' }} />
          <span style={{ fontSize: '21px' }}>Set {badEvent.event_name} Rules</span>
        </div>
      </Header>
      <Body>
        <span>Please select or create {badEvent.event_name}s to define metrics:</span>
        <MappedEventBuilder
          loading={isLoading}
          data={badEvent}
          onMap={handleMapClick}
          footer={null}
          importProps={{
            cloneOptions,
          }}
          cancelText="Reset"
          onCancel={handleReset}
          isBadEvent
        />
      </Body>
    </div>
  );
};

export default BadEventManagement;
